// Copyright 2017 Sorama B.V.

import { RecordingGroupContainer, RecordingGroupMetadata } from "datatypes/Containers";

export type KnownMetadataPropertyName =
    | "name"
    | "notes"
    | "distance"
    | "metadata" // Recording Group and Recording properties
    | "steps"
    | "recordings"
    | "recommendedMeasurementKindName" // Recording Group only properties
    | "projectIds"
    | "measurementTypes"
    | "measurementIds"; // Silverlight export properties

export type RecordingTransformer = (
    key: string,
    value: unknown,
    id?: string,
    idx?: number,
    metadata?: RecordingGroupMetadata
) => { [key: string]: unknown };

export abstract class IRecordingGroupContainerStorageService {
    /**
     * Creates a new RecordingGroupContainer on the disk, loads it with all its initial metadata and returns it.
     */
    public abstract create(): Promise<RecordingGroupContainer>;

    /**
     * Loads a RecordingGroupContainer from the disk with all its metadata and returns it.
     * @param id The id of the RecordingGroupContainer to load.
     */
    public abstract load(id: string): Promise<RecordingGroupContainer>;

    /**
     * Deletes a RecordingGroupContainer and all its Recordings from the disk.
     * @param id The id of the RecordingGroupContainer to delete.
     */
    public abstract delete(id: string): Promise<void>;

    /**
     * Updates the metadata of the RecordingGroupContainer on the disk. Optionally updates the same metadata of all
     * RecordingContainers on the disk which are Recordings of the RecordingGroupContainer if a recordingTransformer is specified.
     * @param id The id of the RecordingGroupContainer.
     * @param key The key of the metadata to update.
     * @param value The value of the metadata to update.
     * @param recordingTransformer A function accepting the value, recording id, recording index and list of recording ids of the recording group
     * and returning an (optionally) transformed value to write into the metadata of the recoring with the specified id.
     * By default, the metadata of recordings is not updated (only the recording group metadata is updated).
     * If you want to write the same metadata value to the recordings, pass value => value.
     */
    public abstract updateMetadata(
        id: string,
        key: string,
        value: unknown,
        recordingTransformer?: RecordingTransformer
    ): Promise<void>;

    /**
     * Updates a collection of metadata of the RecordingGroupContainer on the disk. Optionally updates the same metadata of all
     * RecordingContainers on the disk which are Recordings of the RecordingGroupContainer if recordingTransformers are specified.
     * @param id The id of the RecordingGroupContainer.
     * @param kvps The dictionary with key-value pairs of metadata to update.
     * @param recordingTransformers A dictionary using the same keys as kvps returning a function accepting the value, recording id, recording index and list of recording ids of the recording group
     * and returning an (optionally) transformed value to write into the metadata of the recoring with the specified id.
     * By default, the metadata of recordings is not updated (only the recording group metadata is updated).
     * If you want to write the same metadata value to the recordings, pass value => value.
     */
    public abstract updateMetadataSet(
        id: string,
        kvps: { [key: string]: unknown },
        recordingTransformers?: { [key: string]: RecordingTransformer }
    ): Promise<void>;

    /**
     * Updates a known metadata property of the RecordingGroupContainer on the disk.
     * The service also manages updating the corresponding property of all recordings.
     * @param id The id of the RecordingGroupContainer.
     * @param key The key of the metadata to update.
     * @param value The value of the metadata to update.
     */
    public abstract updateKnownMetadata(id: string, key: KnownMetadataPropertyName, value: unknown): Promise<void>;

    /**
     * Updates known metadata properties of the RecordingGroupContainer on the disk.
     * The service also manages updating the corresponding property of all recordings.
     * @param id The id of the RecordingGroupContainer.
     * @param kvps The dictionary with key-value pairs of known metadata properties to update.
     */
    public abstract updateKnownMetadataSet(id: string, kvps: { [key: string]: unknown }): Promise<void>;

    /**
     * Adds a new or existing Recording to the RecordingGroupContainer.
     * @param id The id of the RecordingGroupContainer.
     * @param existingRecordingId (Optional) The id of an existing Recording to add to the RecordingGroupContainer.
     * If omitted, a new Recording is created.
     */
    public abstract addRecording(id: string, existingRecordingId?: string): Promise<string>;

    /**
     * Delets a Recording from the RecordingGroupContainer and the disk
     * @param id The id of the RecordingGroupContainer.
     * @param recordingId The id ofthe Recording to delete.
     */
    public abstract deleteRecording(id: string, recordingId: string): Promise<void>;
}
